/**
 * SessionFlow component - Orchestrates the session flow.
 *
 * Design: DENiZEN — organic, warm, nature-inspired
 * - Rounded corners, soft shadows
 * - Nature-inspired green palette
 * - WCAG 2.1 AA compliant
 *
 * Flow steps:
 * 1. Start       -> Session type selection
 * 2. Config      -> Session configuration
 * 3. Consent     -> Consent capture
 * 4. Conversation -> Structured dialogue
 * 5. Photo Choice -> Vision editing + grounding image selection before generation
 * 6. (generating / generating_images / image_selection — internal transitions)
 * 7. Image Refinement -> Multi-variant selection and refinement (image / image_refinement)
 * 8. Complete    -> Session summary
 *
 * Back Navigation (issue #175):
 * - Browser back button integrated via useBackButton hook
 * - Confirmation dialog shown during active image generation
 * - In-app back buttons on each step (except start)
 * - State properly cleaned up when navigating backwards
 */

import { useState, useCallback, useEffect, useRef, lazy, Suspense, Component, type ReactNode, type ErrorInfo } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useSession } from '@/context/SessionContext'
import { ConversationProvider } from '@/context/ConversationContext'
import { SessionStart, SessionConfig, MicrophoneTest, type SessionConfigData } from '@/components/session'
import { ConversationPanel } from '@/components/conversation'
import { GenerationProgress, ImageDisplay, ImageFeedback, ImageSelectionGrid, PhotoChoiceScreen, InfluenceDescription } from '@/components/image'
import { GroundingFallbackBanner } from '@/components/image/GroundingFallbackBanner'
import { ImageLightbox } from '@/components/image/ImageLightbox'
import { BackButton } from '@/components/BackButton'
import { BackNavigationDialog } from '@/components/BackNavigationDialog'

// Lazy load heavy image components to reduce initial bundle size
const RefinementFlow = lazy(() => import('@/components/image/RefinementFlow').then(m => ({ default: m.RefinementFlow })))
const ImageSelectionModal = lazy(() => import('@/components/image/ImageSelectionModal').then(m => ({ default: m.ImageSelectionModal })))
import { useSessionCreate, DEFAULT_CONSENT_TEXT } from '@/hooks/useSessionCreate'
import { useConversation } from '@/hooks/useConversation'
import { useImageIteration } from '@/hooks/useImageIteration'
import { useImageGeneration } from '@/hooks/useImageGeneration'
import { useBackButton } from '@/hooks/useBackButton'
import { useGroundingImages, type UseGroundingImagesResult } from '@/hooks/useGroundingImages'
import { useGroundingImagesPreview } from '@/hooks/useGroundingImagesPreview'
import type { GroundingImage } from '@/components/ScrollingPebbleBackground'
import { useInfluenceDescription } from '@/hooks/useInfluenceDescription'
import { parseMarkdown } from '@/utils/parseMarkdown'
import { trackChunkLoadError, trackApiError } from '@/services/telemetry'
import { getApiClient } from '@/services/api'
import type { ImageOption } from '@/services/api'
import { AmbientRecordingPanel } from '@/components/conversation/AmbientRecordingPanel'

const AMBIENT_CONSENT_TEXT = `This session will record ambient audio from a group conversation. Audio is transcribed to text and analysed for community themes. Audio is not stored — only the text transcript is kept. Personal names and identifying details are excluded. All participants have given verbal consent to recording.`

/**
 * ErrorBoundary for lazy-loaded components.
 *
 * Catches chunk load failures (network issues, CDN problems) and
 * renders a user-friendly error message with retry option.
 */
interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  retryKey: number
}

class LazyLoadErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, retryKey: 0 }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[LazyLoadErrorBoundary] Component failed to load', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })

    // Track error to Application Insights via backend (fire-and-forget)
    trackChunkLoadError(error, 'SessionFlow').catch((telemetryError) => {
      // Telemetry failures shouldn't affect users, but log for developer awareness
      console.warn('[LazyLoadErrorBoundary] Telemetry send failed:', telemetryError)
    })
  }

  handleRetry = (): void => {
    // Increment retryKey to force React to re-mount children and re-attempt chunk load
    this.setState(prev => ({
      hasError: false,
      error: null,
      retryKey: prev.retryKey + 1,
    }))
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="denizen-card p-6 text-center bg-error/5">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-error/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-display font-semibold text-dark-green mb-2">
            Failed to Load
          </h3>
          <p className="text-sm font-body text-muted mb-4">
            This component couldn&apos;t be loaded. Please check your connection and try again.
          </p>
          <button
            onClick={this.handleRetry}
            className="btn-denizen-secondary px-6 py-2"
          >
            Try Again
          </button>
        </div>
      )
    }

    // Use retryKey as key to force React to re-mount children on retry
    // This ensures lazy() re-attempts the dynamic import
    return <div key={this.state.retryKey}>{this.props.children}</div>
  }
}

/**
 * Session flow step
 */
export type SessionFlowStep =
  | 'start'
  | 'config'
  | 'consent'
  | 'mic_test'
  | 'conversation'
  | 'photo_choice'        // Vision editing + grounding image selection before generation
  | 'generating'
  | 'generating_images'
  | 'image_selection'
  | 'image_refinement'    // Multi-variant refinement flow
  | 'image'
  | 'email_capture'
  | 'complete'

export interface SessionFlowProps {
  /**
   * Facilitator ID
   */
  facilitatorId: string
  /**
   * Whether to show the ambient recording option (only when joined via event code)
   */
  showAmbient?: boolean
  /**
   * Callback when session is completed
   */
  onComplete?: (sessionId: string) => void
  /**
   * Called whenever grounding images change so the parent layout can show
   * community photo circles in the background.
   */
  onGroundingImagesReady?: (images: GroundingImage[]) => void
  /**
   * Called whenever the active step changes so the parent can adjust layout
   * chrome (e.g. hide background pebbles after the first three screens).
   */
  onStepChange?: (step: SessionFlowStep) => void
}

export function SessionFlow({ facilitatorId, showAmbient, onComplete, onGroundingImagesReady, onStepChange }: SessionFlowProps) {
  const [currentStep, setCurrentStep] = useState<SessionFlowStep>('start')
  const [sessionType, setSessionType] = useState<'individual' | 'group' | 'ambient'>('individual')
  const [micAvailable, setMicAvailable] = useState(true)

  const { isLoading: sessionLoading, error: sessionError } = useSession()

  // Event context passed via router state from the join flow
  const routerLocation = useLocation()
  const joinEventContext = (routerLocation.state as { eventId?: string; groundingImageIds?: string[] } | null) ?? null

  const sessionCreate = useSessionCreate()

  // These hooks need session ID, so we initialise them conditionally
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [participantId, setParticipantId] = useState<string>('')

  const conversation = useConversation(sessionId, participantId)
  const imageIteration = useImageIteration(sessionId)
  const imageGeneration = useImageGeneration(sessionId)
  const {
    cancel: cancelGeneration,
    reset: resetGeneration,
    isLoading: isGenerationLoading,
    selectVariation,
    refine: refineGeneration,
    accept: acceptGeneration,
    currentRound,
    currentVariations,
    rounds: generationRounds,
    maxRounds,
    canRefine,
    status: generationStatus,
    error: generationError,
    elapsedMs: generationElapsedMs,
    canGoBack: generationCanGoBack,
    goBack: generationGoBack,
    navigateToRound: generationNavigateToRound,
  } = imageGeneration

  const previewImages = useGroundingImagesPreview(!sessionId)
  const sessionGroundingImages = useGroundingImages(sessionId)
  const groundingImages: UseGroundingImagesResult = sessionId
    ? sessionGroundingImages
    : { ...previewImages, location: '', refresh: () => {} }

  // Notify parent layout so it can show community photo circles in the background.
  // Guard with sessionId: the preview hook (no session) fetches default-location
  // Geograph photos which would overwrite the random FLUX images on the landing screen.
  // By the time a session exists, background pebbles are hidden (post-consent step).
  useEffect(() => {
    if (onGroundingImagesReady && sessionId && groundingImages.images.length > 0) {
      onGroundingImagesReady(groundingImages.images)
    }
  }, [groundingImages.images, onGroundingImagesReady, sessionId])

  // Notify parent layout whenever the active step changes
  useEffect(() => {
    onStepChange?.(currentStep)
  }, [currentStep, onStepChange])

  const influenceDescription = useInfluenceDescription(sessionId)

  // Fetch influence description once after first generation round completes
  const influenceFetchedRef = useRef(false)
  const fetchInfluence = influenceDescription.fetch
  useEffect(() => {
    if (
      currentRound >= 1 &&
      !isGenerationLoading &&
      generationStatus === 'complete' &&
      !influenceFetchedRef.current
    ) {
      influenceFetchedRef.current = true
      void fetchInfluence()
    }
  }, [currentRound, isGenerationLoading, generationStatus, fetchInfluence])

  // Facilitator-edited vision description (set during photo_choice step)
  const [editedVisionDescription, setEditedVisionDescription] = useState<string | null>(null)
  // Selected grounding image ID (preserved across retry attempts)
  const [selectedGroundingImageId, setSelectedGroundingImageId] = useState<string | null>(null)
  // Vision description (or summary fallback) for editable textarea during photo choice
  const [conversationSummary, setConversationSummary] = useState<string>('')

  // State for modify conversation error feedback (issue #249)
  const [modifyConversationError, setModifyConversationError] = useState<string | null>(null)

  // State for early completion (facilitator shortcut, issue #197)
  const [isEarlyCompleting, setIsEarlyCompleting] = useState(false)
  const [earlyCompleteError, setEarlyCompleteError] = useState<string | null>(null)
  const earlyCompleteInFlightRef = useRef(false)

  // State for confirmation button errors (issue #250)
  const [confirmationError, setConfirmationError] = useState<string | null>(null)
  const confirmationErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timer on unmount to prevent state updates on dead components
  useEffect(() => {
    return () => {
      if (confirmationErrorTimerRef.current) clearTimeout(confirmationErrorTimerRef.current)
    }
  }, [])

  /** Set a transient confirmation error that auto-clears after 5 seconds */
  const setTransientConfirmationError = useCallback((msg: string) => {
    if (confirmationErrorTimerRef.current) clearTimeout(confirmationErrorTimerRef.current)
    setConfirmationError(msg)
    confirmationErrorTimerRef.current = setTimeout(() => setConfirmationError(null), 5000)
  }, [])

  /**
   * Shared catch-block logic for confirmation button handlers.
   * Keeps buttons visible (no state clear), suppresses the hook's error channel,
   * shows a transient message, and fires telemetry. (#250)
   */
  const handleConfirmationError = useCallback((err: unknown, endpoint: string) => {
    // Suppress conversation.error so confirmationError is the sole feedback channel (#250)
    conversation.clearError()
    const isSessionError = err instanceof Error && err.message === 'No active session'
    setTransientConfirmationError(
      isSessionError
        ? 'Your session has expired. Please refresh the page to start again.'
        : 'Something went wrong. Please try again.'
    )
    const statusCode = (err as { response?: { status?: number } })?.response?.status
    trackApiError(
      err instanceof Error ? err : new Error(String(err)),
      endpoint,
      sessionId ?? undefined,
      statusCode,
    ).catch((telemetryErr) => console.warn('[SessionFlow] Telemetry send failed:', telemetryErr))
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [conversation.clearError, sessionId, setTransientConfirmationError])

  // State for selected image from the grid
  const [selectedImage, setSelectedImage] = useState<ImageOption | null>(null)
  // State for tracking image generation progress (for inline display)
  const [isGeneratingImages, setIsGeneratingImages] = useState(false)
  // State for showing the full-screen image selection modal
  const [showImageModal, setShowImageModal] = useState(false)

  // --- Back navigation state (issue #175) ---
  const [showBackDialog, setShowBackDialog] = useState(false)
  const [backDialogFromStep, setBackDialogFromStep] = useState<SessionFlowStep>('start')

  /**
   * Handle back navigation when allowed (no confirmation needed).
   * Returns true if the step change was handled, false to block.
   */
  const handleBack = useCallback((fromStep: SessionFlowStep): boolean => {
    switch (fromStep) {
      case 'config':
        setCurrentStep('start')
        return true

      case 'consent':
        setCurrentStep('config')
        return true

      case 'mic_test':
        // Session already created — block back navigation (same as conversation)
        return false

      case 'conversation':
        // Going back from conversation to consent would lose the session.
        // Since a session has already been created, we block this.
        // In future we could add a "are you sure?" but for now, block it.
        return false

      case 'photo_choice':
        // Return to conversation - the conversation state is preserved
        setIsGeneratingImages(false)
        setCurrentStep('conversation')
        return true

      case 'image_refinement':
        // This needs confirmation (handled by onConfirmNeeded) - should not reach here
        return false

      case 'image':
        // Return to image refinement - image state is preserved
        setCurrentStep('image_refinement')
        return true

      case 'image_selection':
        setCurrentStep('conversation')
        return true

      case 'complete':
        // Going back from complete doesn't make sense (session is finished)
        return false

      default:
        return false
    }
  }, [])

  /**
   * Handle confirmation needed for back navigation during critical steps.
   * Shows the confirmation dialog.
   */
  const handleConfirmNeeded = useCallback((fromStep: SessionFlowStep) => {
    setBackDialogFromStep(fromStep)
    setShowBackDialog(true)
  }, [])

  /**
   * Handle confirmed back navigation (user pressed "Go back" in dialog).
   * Performs cleanup and state transitions for critical steps.
   */
  const handleBackConfirmed = useCallback(() => {
    setShowBackDialog(false)

    switch (backDialogFromStep) {
      case 'generating':
      case 'generating_images':
        // Cancel generation and return to photo choice
        cancelGeneration()
        setIsGeneratingImages(false)
        setCurrentStep('photo_choice')
        break

      case 'image_refinement':
        // If generation is in progress, cancel it first
        if (isGenerationLoading) {
          cancelGeneration()
        }
        // Reset image generation state and return to photo choice
        resetGeneration()
        setIsGeneratingImages(false)
        setCurrentStep('photo_choice')
        break

      default:
        // Fallback: try the normal back handler
        handleBack(backDialogFromStep)
        break
    }
  }, [backDialogFromStep, cancelGeneration, isGenerationLoading, resetGeneration, handleBack])

  /**
   * Handle dialog cancel (user chose to stay)
   */
  const handleBackCancelled = useCallback(() => {
    setShowBackDialog(false)
  }, [])

  // Browser back button integration
  const { canGoBack, goBack } = useBackButton({
    currentStep,
    isGenerating: isGenerationLoading || isGeneratingImages,
    onBack: handleBack,
    onConfirmNeeded: handleConfirmNeeded,
  })

  // Handle session type selection
  const handleSessionTypeSelect = useCallback((type: 'individual' | 'group' | 'ambient') => {
    if (type === 'ambient') {
      setSessionType('ambient')
      // Create session immediately, skipping config/consent/mic_test
      const ambientConfig = {
        sessionType: 'ambient' as const,
        participantCount: 1,
        location: 'ambient-recording',
        facilitatorId,
      }
      sessionCreate.setSessionType('ambient')
      sessionCreate.setConfig(ambientConfig)

      const pid = crypto.randomUUID()
      setParticipantId(pid)
      void sessionCreate.createSession(true, AMBIENT_CONSENT_TEXT, pid).then((response) => {
        setSessionId(response.session_id)
        setCurrentStep('conversation')
      }).catch((err: unknown) => {
        console.error('Ambient session creation failed:', err)
        setCurrentStep('start')
      })
      return
    }

    setSessionType(type)
    sessionCreate.setSessionType(type)
    setCurrentStep('config')
  }, [sessionCreate, facilitatorId])

  // Handle session configuration
  const handleConfigConfirm = useCallback((config: SessionConfigData) => {
    sessionCreate.setConfig(config)
    setCurrentStep('consent')
  }, [sessionCreate])

  // Handle consent and session creation
  const handleConsentGiven = useCallback(async (consentGiven: boolean) => {
    if (!sessionCreate.config) return

    // Generate participant ID as proper UUID (backend requires UUID format)
    const newParticipantId = crypto.randomUUID()
    setParticipantId(newParticipantId)

    try {
      if (consentGiven) {
        const eventContext = (joinEventContext?.eventId && joinEventContext.groundingImageIds)
          ? { eventId: joinEventContext.eventId, groundingImageIds: joinEventContext.groundingImageIds }
          : undefined
        const response = await sessionCreate.createSession(
          true,
          DEFAULT_CONSENT_TEXT,
          newParticipantId,
          eventContext
        )

        // Use the actual session ID from the API response
        setSessionId(response.session_id)
        setCurrentStep('mic_test')
      } else {
        // Consent declined - show declined message
        setCurrentStep('start')
      }
    } catch (error) {
      console.error('Session creation failed:', error)
    }
  }, [sessionCreate, joinEventContext])

  // Handle conversation message (with optional image)
  const handleSendMessage = useCallback(async (content: string, imageData?: string) => {
    const inputMode = imageData ? 'image' : 'text'

    const response = await conversation.sendMessage(content, inputMode, imageData)

    // If we're now generating images, show generating state
    if (response.dialogue_complete && !response.awaiting_image_confirmation) {
      setIsGeneratingImages(true)
    }

    // Once images arrive, stop showing generating state
    if (response.dialogue_complete && response.image_options && response.image_options.length > 0) {
      setIsGeneratingImages(false)
      // Stay in conversation - images will show inline
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [conversation.sendMessage])

  // Handle image generation confirmation - go to photo choice (vision editing + image source)
  const handleConfirmImageGeneration = useCallback(() => {
    // Guard against double-invocation (e.g., rapid button clicks)
    if (currentStep !== 'conversation') return
    // Prefer clean vision description (no conversational framing) for the editable textarea.
    // Falls back to conversation summary if vision extraction failed.
    // Uses || (not ??) to also catch empty strings from Pydantic defaults.
    const visionText = conversation.visionDescription || conversation.conversationSummary
    if (visionText?.trim()) {
      setConversationSummary(visionText)
    }
    // CRITICAL: Immediately disable STT before any navigation (fixes #111, #112, #114)
    // This ensures the microphone stops recording before we leave the conversation step
    setIsGeneratingImages(true)
    // Refresh grounding images — backend may have prefetched during conversation
    sessionGroundingImages.refresh()
    setCurrentStep('photo_choice')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- .refresh is a stable ref
  }, [currentStep, conversation.visionDescription, conversation.conversationSummary, sessionGroundingImages.refresh])

  // Handle photo choice confirmation (text-to-image with edited vision description)
  const handlePhotoConfirm = useCallback((editedDescription: string) => {
    setEditedVisionDescription(editedDescription)
    setSelectedGroundingImageId(null)
    setCurrentStep('image_refinement')
    void imageGeneration.generateInitial(
      undefined,
      editedDescription,
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [imageGeneration.generateInitial])

  // Handle grounding image selection (Tier 1/2 → image-to-image generation)
  const handleGroundingImageSelect = useCallback((imageId: string, editedDescription: string) => {
    setEditedVisionDescription(editedDescription)
    setSelectedGroundingImageId(imageId)
    setCurrentStep('image_refinement')
    // Pre-warm CDN cache (best-effort, non-blocking)
    if (sessionGroundingImages.images) {
      const img = sessionGroundingImages.images.find((i: { id: string }) => i.id === imageId)
      if (img?.image_url) {
        fetch(img.image_url, { method: 'HEAD', mode: 'no-cors' }).catch(() => {})
      }
    }
    void imageGeneration.generateInitial(
      'Create variations based on this location photo and our conversation',
      editedDescription,
      imageId,
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [imageGeneration.generateInitial, sessionGroundingImages.images])

  // Handle variation selection in refinement flow
  const handleVariationSelect = useCallback((index: number) => {
    selectVariation(index)
  }, [selectVariation])

  // Handle refinement in new multi-variant flow
  const handleRefinementRefine = useCallback((feedback: string) => {
    void refineGeneration(feedback)
  }, [refineGeneration])

  // Handle accept in refinement flow
  const handleRefinementAccept = useCallback(() => {
    const acceptedVariation = acceptGeneration()
    if (acceptedVariation) {
      // Convert ImageVariation to ImageOption for compatibility
      setSelectedImage({
        image_id: acceptedVariation.image_id,
        image_url: acceptedVariation.image_url,
      })
      // Fire-and-forget: persist the selection on the backend
      if (sessionId && acceptedVariation.image_id) {
        const apiClient = getApiClient()
        apiClient.selectImage(sessionId, acceptedVariation.image_id).catch(err => {
          console.warn('[SessionFlow] Failed to persist image selection:', err)
        })
      }
      setCurrentStep('email_capture')
    }
  }, [acceptGeneration, sessionId])

  // Handle retry after generation failure
  const handleGenerationRetry = useCallback(() => {
    // Reset STT disabled state so user can use voice input again
    setIsGeneratingImages(false)
    // If we haven't generated anything yet (round 0), retry initial generation
    // preserving any facilitator edits to the vision description AND grounding image
    if (imageGeneration.currentRound === 0) {
      void imageGeneration.generateInitial(
        selectedGroundingImageId
          ? 'Create variations based on this location photo and our conversation'
          : undefined,
        editedVisionDescription ?? undefined,
        selectedGroundingImageId ?? undefined,
      )
    } else {
      // Otherwise go back to photo choice to start fresh
      setCurrentStep('photo_choice')
      imageGeneration.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn/value refs, not whole object
  }, [imageGeneration.currentRound, imageGeneration.generateInitial, imageGeneration.reset, editedVisionDescription, selectedGroundingImageId])

  // Handle going back to previous round
  const handleGoBack = useCallback(() => {
    generationGoBack()
  }, [generationGoBack])

  // Handle navigating to a specific round in the timeline
  const handleNavigateToRound = useCallback((roundIndex: number) => {
    generationNavigateToRound(roundIndex)
  }, [generationNavigateToRound])

  // Handle modify conversation (user wants to add more during image confirmation)
  const handleModifyConversation = useCallback(async () => {
    try {
      await conversation.sendMessage("Actually, I'd like to add something more.", 'text')
    } catch (err) {
      console.error('[SessionFlow] Modify conversation failed:', err)
      setModifyConversationError('Could not return to chat. Please try again.')
      setTimeout(() => setModifyConversationError(null), 5000)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [conversation.sendMessage])

  // Handle participant confirming they're ready to create their image (issue #173)
  // After the confirmation message succeeds, transition directly to photo_choice
  // (same as handleConfirmImageGeneration but preceded by sending the "I'm ready" turn).
  const handleConfirmReady = useCallback(async () => {
    try {
      // Read vision_description from the response itself — conversation.visionDescription
      // would be a stale-closure read here (useCallback captures values from this render,
      // and the setState inside sendMessage hasn't flushed yet at this point).
      const response = await conversation.sendMessage("I'm ready", 'text')
      setConfirmationError(null)
      const visionText = response.vision_description || response.conversation_summary
      if (visionText?.trim()) {
        setConversationSummary(visionText)
      }
      setIsGeneratingImages(true)
      sessionGroundingImages.refresh()
      setCurrentStep('photo_choice')
    } catch (err) {
      console.error('[SessionFlow] Confirm ready failed:', err)
      handleConfirmationError(err, 'confirmation/ready')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [conversation.sendMessage, sessionGroundingImages.refresh, handleConfirmationError])

  // Handle participant wanting to add more to their vision (issue #173)
  // Clear awaitingFinalConfirmation immediately so the text input unlocks without waiting for backend
  const handleAddMore = useCallback(async () => {
    conversation.clearFinalConfirmation()
    try {
      await conversation.sendMessage("I'd like to continue sharing my thoughts.", 'text')
      setConfirmationError(null)
    } catch (err) {
      console.error('[SessionFlow] Add more failed:', err)
      handleConfirmationError(err, 'confirmation/add-more')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use specific fn ref, not whole object
  }, [conversation.clearFinalConfirmation, conversation.sendMessage, handleConfirmationError])

  // Handle early conversation completion (facilitator shortcut, issue #197)
  const handleEarlyComplete = useCallback(async () => {
    if (!sessionId || isEarlyCompleting || earlyCompleteInFlightRef.current) return
    earlyCompleteInFlightRef.current = true

    setIsEarlyCompleting(true)
    try {
      const apiClient = getApiClient()
      const result = await apiClient.completeEarly(sessionId)

      // Prefer clean vision description for the editable textarea; fall back to summary.
      // Uses || (not ??) to also catch empty strings from Pydantic defaults.
      const visionText = result.vision_description || result.conversation_summary
      if (visionText?.trim()) {
        setConversationSummary(visionText)
      }

      // Disable STT and transition to photo choice (same as normal completion)
      setIsGeneratingImages(true)
      sessionGroundingImages.refresh()
      setCurrentStep('photo_choice')
    } catch (error) {
      console.error('[SessionFlow] Early completion failed:', error)
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 400) {
        // Frontend/backend turn count disagreement - let the user know and continue conversation
        console.warn('[SessionFlow] Backend rejected: insufficient turns')
        setEarlyCompleteError('A few more messages are needed before creating your image.')
        setTimeout(() => setEarlyCompleteError(null), 5000)
      } else {
        // Show a brief error indicator - the conversation panel is still functional
        setEarlyCompleteError('Could not create image right now. Please try again.')
        setTimeout(() => setEarlyCompleteError(null), 5000)
      }
    } finally {
      setIsEarlyCompleting(false)
      earlyCompleteInFlightRef.current = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- .refresh is a stable ref
  }, [sessionId, isEarlyCompleting, sessionGroundingImages.refresh])

  // Handle image selection from grid (and modal)
  const handleImageSelect = useCallback((image: ImageOption) => {
    setSelectedImage(image)
    setShowImageModal(false)
    // Fire-and-forget: persist the selection on the backend
    if (sessionId && image.image_id) {
      const apiClient = getApiClient()
      apiClient.selectImage(sessionId, image.image_id).catch(err => {
        console.warn('[SessionFlow] Failed to persist image selection:', err)
      })
    }
    setCurrentStep('email_capture')
  }, [sessionId])

  // Handle image acceptance (legacy single-image iteration flow)
  const handleImageAccept = useCallback(() => {
    imageIteration.accept()
    // Fire-and-forget: persist the selection on the backend
    if (sessionId && imageIteration.currentImage?.image_id) {
      const apiClient = getApiClient()
      apiClient.selectImage(sessionId, imageIteration.currentImage.image_id).catch(err => {
        console.warn('[SessionFlow] Failed to persist image selection:', err)
      })
    }
    setCurrentStep('email_capture')
  }, [imageIteration, sessionId])

  // Handle image refinement
  const handleImageRefine = useCallback(async (feedback: string) => {
    setCurrentStep('generating')
    await imageIteration.refine(feedback)
    setCurrentStep('image')
  }, [imageIteration])

  /**
   * Determine whether to show the back button for the current step.
   * We show it on steps where the user can meaningfully go back,
   * but not on 'start' (nowhere to go), 'config' (SessionConfig provides its own
   * inline back), 'conversation' (session is created, going back would orphan it),
   * or 'complete' (session is finished).
   */
  const showBackButton = canGoBack && !['start', 'config', 'mic_test', 'conversation', 'complete'].includes(currentStep)

  /**
   * Get the label for the back button based on the current step.
   * Only 'photo_choice' needs a distinct label; all other visible steps
   * (those not excluded by showBackButton) use the default 'Back'.
   */
  const getBackLabel = (): string => {
    if (currentStep === 'photo_choice') return 'Back to chat'
    return 'Back'
  }

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 'start':
        return (
          <SessionStart
            onStart={handleSessionTypeSelect}
            isLoading={sessionLoading}
            error={sessionError || sessionCreate.error}
            showAmbient={showAmbient}
          />
        )

      case 'config':
        return (
          <SessionConfig
            sessionType={sessionType}
            facilitatorId={facilitatorId}
            onConfirm={handleConfigConfirm}
            onBack={() => setCurrentStep('start')}
            isLoading={sessionLoading}
            error={sessionError}
          />
        )

      case 'consent':
        return (
          <ConsentForm
            consentText={DEFAULT_CONSENT_TEXT}
            onConsent={(consented) => void handleConsentGiven(consented)}
            isLoading={sessionCreate.isLoading}
            error={sessionCreate.error}
          />
        )

      case 'mic_test':
        return (
          <MicrophoneTest
            onContinue={(passed) => {
              setMicAvailable(passed)
              setCurrentStep('conversation')
            }}
          />
        )

      case 'conversation':
        if (sessionType === 'ambient' && sessionId) {
          const apiClient = getApiClient()
          return (
            <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6 sm:px-12">
              <AmbientRecordingPanel
                sessionId={sessionId}
                sendChunk={(sid, audio, idx) => apiClient.sendAmbientAudioChunk(sid, audio, idx)}
                stopAmbient={(sid) => apiClient.stopAmbientRecording(sid)}
                extractAmbient={(sid) => apiClient.extractAmbientThemes(sid)}
                onFinish={() => {
                  setCurrentStep('complete')
                }}
              />
            </div>
          )
        }
        return (
          <div className="flex-1 min-h-0">
            <ConversationPanel
              turns={conversation.turns}
              onSendMessage={(content, imageData) => void handleSendMessage(content, imageData)}
              isLoading={conversation.isLoading}
              isDialogueComplete={conversation.isDialogueComplete}
              progress={{
                topicsCovered: conversation.topicCoverage.topicsCovered,
                topicsRequired: conversation.topicCoverage.topicsRequired,
                isComplete: conversation.isDialogueComplete,
                coveredTopics: conversation.topicCoverage.coveredTopics,
              }}
              error={conversation.error}
              inputType={conversation.pendingInputType}
              inputOptions={conversation.pendingInputOptions}
              isGeneratingImages={isGeneratingImages}
              imageOptions={conversation.imageOptions}
              onImageSelect={handleImageSelect}
              awaitingImageConfirmation={conversation.awaitingImageConfirmation}
              onConfirmImageGeneration={handleConfirmImageGeneration}
              onModifyConversation={() => void handleModifyConversation()}
              modifyConversationError={modifyConversationError}
              imageGenerationUnavailable={conversation.imageGenerationUnavailable}
              transcribeAudio={micAvailable ? conversation.transcribeAudio : undefined}
              awaitingFinalConfirmation={conversation.awaitingFinalConfirmation}
              onConfirmReady={() => void handleConfirmReady()}
              onAddMore={() => void handleAddMore()}
              onEarlyComplete={() => void handleEarlyComplete()}
              isEarlyCompleting={isEarlyCompleting}
              earlyCompleteError={earlyCompleteError}
              confirmationError={confirmationError}
            />
          </div>
        )

      case 'photo_choice':
        return (
          <PhotoChoiceScreen
            onConfirm={handlePhotoConfirm}
            conversationSummary={conversationSummary}
            groundingImages={groundingImages.images}
            groundingImagesLoading={groundingImages.isLoading}
            groundingLocation={groundingImages.location}
            onGroundingImageSelect={handleGroundingImageSelect}
            groundingError={groundingImages.error}
          />
        )

      case 'image_refinement':
        return (
          <>
            {/* Show fallback banner when grounding image couldn't be used */}
            {generationRounds.length > 0 && !isGenerationLoading && (
              <GroundingFallbackBanner
                key="grounding-banner"
                groundingApplied={generationRounds[generationRounds.length - 1]?.groundingApplied}
                generationMode={generationRounds[generationRounds.length - 1]?.generationMode}
                roundCount={generationRounds.length}
              />
            )}
            <LazyLoadErrorBoundary key="refinement-boundary">
              <Suspense fallback={<div className="animate-pulse bg-stone h-96 rounded-2xl" />}>
                <RefinementFlow
                  variations={currentVariations}
                  selectedIndex={generationRounds[generationRounds.length - 1]?.selectedVariantIndex ?? null}
                  onSelectVariation={handleVariationSelect}
                  onAccept={handleRefinementAccept}
                  onRefine={handleRefinementRefine}
                  currentRound={currentRound}
                  maxRounds={maxRounds}
                  canRefine={canRefine}
                  status={generationStatus}
                  isLoading={isGenerationLoading}
                  error={generationError}
                  elapsedMs={generationElapsedMs}
                  onRetry={handleGenerationRetry}
                  onGoBack={handleGoBack}
                  canGoBack={generationCanGoBack}
                  rounds={generationRounds}
                  onNavigateToRound={handleNavigateToRound}
                  transcribeAudio={micAvailable ? conversation.transcribeAudio : undefined}
                />
              </Suspense>
            </LazyLoadErrorBoundary>

            {/* Show influence description after first generation completes */}
            {currentRound >= 1 && !isGenerationLoading && (
              <div key="influence-section" className="mt-6">
                <InfluenceDescription
                  description={influenceDescription.description}
                  isLoading={influenceDescription.isLoading}
                  error={influenceDescription.error}
                />
              </div>
            )}
          </>
        )

      case 'generating':
        return (
          <GenerationProgress
            status={imageIteration.isLoading ? 'generating' : 'validating'}
            iterationNumber={imageIteration.currentIteration + 1}
            isRefinement={imageIteration.currentIteration > 0}
          />
        )

      case 'generating_images':
        return (
          <div className="animate-slide-up">
            <ImageSelectionGrid
              images={[]}
              onSelect={() => {}}
              isLoading={false}
              isGenerating={true}
            />
          </div>
        )

      case 'image_selection':
        return (
          <div className="animate-slide-up">
            {conversation.imageOptions && conversation.imageOptions.length > 0 ? (
              <ImageSelectionGrid
                images={conversation.imageOptions}
                onSelect={handleImageSelect}
                isLoading={false}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-muted">No images available</p>
              </div>
            )}
          </div>
        )

      case 'image':
        return (
          <div className="space-y-6">
            {imageIteration.currentImage && (
              <>
                <ImageDisplay
                  imageUrl={imageIteration.currentImage.image_url}
                  alt="Your vision for the future"
                  title="Your Community Vision"
                  iterationNumber={imageIteration.currentIteration}
                />
                <ImageFeedback
                  iterationNumber={imageIteration.currentIteration}
                  maxIterations={imageIteration.maxIterations}
                  onAccept={handleImageAccept}
                  onRefine={(feedback) => void handleImageRefine(feedback)}
                  isLoading={imageIteration.isLoading}
                  error={imageIteration.error}
                />
              </>
            )}
          </div>
        )

      case 'email_capture':
        return (
          <EmailCaptureStep
            sessionId={sessionId || ''}
            onComplete={(submitted) => {
              if (submitted && sessionId) {
                onComplete?.(sessionId)
              }
              setCurrentStep('complete')
              if (!submitted && sessionId) {
                onComplete?.(sessionId)
              }
            }}
          />
        )

      case 'complete':
        if (sessionType === 'ambient') {
          return (
            <div className="px-8 py-6 sm:px-12 text-center animate-slide-up">
              <div className="w-24 h-24 mx-auto mb-8 bg-mid-green rounded-full border border-dark-green/15 shadow-denizen flex items-center justify-center animate-stamp">
                <svg className="w-12 h-12 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-display font-bold text-dark-green mb-3">
                Themes Captured
              </h2>
              <p className="text-dark-green/70 mb-8 max-w-md mx-auto">
                Community themes have been extracted and saved from your group conversation.
              </p>
              <button
                onClick={() => {
                  setCurrentStep('start')
                  sessionCreate.reset()
                  setSessionId(null)
                }}
                className="btn-denizen-primary px-8 py-3"
              >
                Start New Session
              </button>
            </div>
          )
        }
        return (
          <SessionComplete
            sessionId={sessionId || ''}
            imageUrl={selectedImage?.image_url || imageIteration.currentImage?.image_url || null}
            onNewSession={() => {
              setCurrentStep('start')
              sessionCreate.reset()
              conversation.reset()
              imageIteration.reset()
              resetGeneration()
              setSessionId(null)
              setSelectedImage(null)
              setConversationSummary('')
              setEditedVisionDescription(null)
              setIsGeneratingImages(false)
              setIsEarlyCompleting(false)
              influenceFetchedRef.current = false
            }}
            onGoBack={generationRounds.length > 0 ? () => {
              setSelectedImage(null)
              setCurrentStep('image_refinement')
            } : undefined}
            influenceDescription={influenceDescription.description}
          />
        )

      default:
        return null
    }
  }

  // Conversation step uses full width, other steps use centred narrow layout
  const isConversationStep = currentStep === 'conversation'
  const setupSteps: SessionFlowStep[] = ['start', 'config', 'consent', 'mic_test', 'photo_choice']
  const regionLabel = setupSteps.includes(currentStep) ? 'Session setup' : 'Session progress'

  return (
    <ConversationProvider>
      <div
        className={
          isConversationStep
            ? 'h-full flex flex-col w-full px-4 sm:px-6 py-2'
            : 'flex-1 min-h-0 flex flex-col'
        }
        {...(!isConversationStep && {
          tabIndex: -1,
          role: 'region' as const,
          'aria-label': regionLabel,
        })}
      >
        {isConversationStep ? (
          <>
            {showBackButton && (
              <div className="mb-4 animate-slide-up">
                <BackButton onClick={goBack} label={getBackLabel()} disabled={false} />
              </div>
            )}
            {renderStep()}
          </>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto [overflow-anchor:none] px-8">
            <div className="flex flex-col pt-5 pb-6">
              {/* Top bar: back button left + logo right — hidden on the home start screen */}
              {currentStep !== 'start' && (
                <div className="flex items-start justify-between mb-3">
                  <div>
                    {showBackButton && (
                      <div className="animate-slide-up">
                        <BackButton onClick={goBack} label={getBackLabel()} disabled={false} />
                      </div>
                    )}
                  </div>
                  <img
                    src="/images/brand/DenizenLogoSmall.png"
                    alt="DENiZEN"
                    className="w-20 flex-shrink-0"
                  />
                </div>
              )}
              <div>
                {renderStep()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full-screen image selection modal */}
      <LazyLoadErrorBoundary>
        <Suspense fallback={null}>
          <ImageSelectionModal
            isOpen={showImageModal}
            images={conversation.imageOptions || []}
            onSelect={handleImageSelect}
            isGenerating={isGeneratingImages}
            isLoading={false}
          />
        </Suspense>
      </LazyLoadErrorBoundary>

      {/* Back navigation confirmation dialog (issue #175) */}
      <BackNavigationDialog
        isOpen={showBackDialog}
        fromStep={backDialogFromStep}
        isGenerating={isGenerationLoading || isGeneratingImages}
        onConfirm={handleBackConfirmed}
        onCancel={handleBackCancelled}
      />
    </ConversationProvider>
  )
}

/**
 * Consent form component — DENiZEN
 */
interface ConsentFormProps {
  consentText: string
  onConsent: (consented: boolean) => void
  isLoading?: boolean
  error?: string | null
}

function ConsentForm({ consentText, onConsent, isLoading, error }: ConsentFormProps) {
  const [agreed, setAgreed] = useState(false)

  const renderConsentText = (text: string) => {
    return text.split('\n').map((line, index) => {
      const isListItem = /^\d+\./.test(line)
      const className = isListItem ? 'mb-3' : line.trim() ? 'mb-2' : 'mb-2'

      return (
        <div key={index} className={className}>
          {parseMarkdown(line, 'font-display font-semibold text-dark-green')}
        </div>
      )
    })
  }

  return (
    <div className="max-w-3xl mx-auto px-6 animate-slide-up">
      {/* "Session Setup" heading — per Figma node 1:1053 */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-display font-bold text-dark-green mb-2">
          Session Setup
        </h2>
        <p className="text-base font-body text-dark-green/80">
          Please read and confirm your consent to participate
        </p>
      </div>

      {/* Separator — per Figma, ~40px gap before content */}
      <hr className="border-dark-green/15 mb-8" />

      {/* Consent text — scrollable region with constrained height */}
      <div
        className="mb-6 max-h-72 overflow-y-auto border border-dark-green/20 rounded-[10px] p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#40aa3b] focus-visible:ring-offset-1"
        tabIndex={0}
        role="region"
        aria-label="Research information — scroll to read"
      >
        <h3 className="font-body font-semibold text-dark-green mb-4">
          Research Information
        </h3>
        <div className="font-body text-dark-green text-base leading-relaxed">
          {renderConsentText(consentText)}
        </div>
      </div>

      {/* Checkbox row — standalone per Figma (not a styled card) */}
      <label className="flex items-start gap-4 cursor-pointer mb-5">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="checkbox-denizen mt-1 flex-shrink-0"
          aria-required="true"
        />
        <span className="text-base font-body text-dark-green leading-relaxed">
          I have read and understand the above information, and I consent to participate in this research study.
          <span className="text-error ml-1 font-bold" aria-hidden="true">*</span>
        </span>
      </label>

      {/* Buttons — centred pair: Decline (outline) + I agree → (solid) */}
      <div className="flex gap-4 max-w-md mx-auto">
        <button
          onClick={() => onConsent(false)}
          disabled={isLoading}
          className="btn-denizen-secondary flex-1 py-4"
        >
          Decline
        </button>
        <button
          onClick={() => onConsent(true)}
          disabled={isLoading || !agreed}
          className="btn-denizen-primary flex-1 py-4"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Starting...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              I agree
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          )}
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 mt-6 bg-error/5 border border-error/20 rounded-2xl">
          <p className="text-sm font-body text-dark-green mb-2">
            <strong>Something went wrong:</strong> {error}
          </p>
          <p className="text-xs font-body text-muted">
            Please try again. If the problem persists, refresh the page.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Optional email capture step shown after image selection.
 * Participant may skip — email is never required.
 */
interface EmailCaptureStepProps {
  sessionId: string
  onComplete: (submitted: boolean) => void
}

function EmailCaptureStep({ sessionId, onComplete }: EmailCaptureStepProps) {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setIsSubmitting(true)
    setError(null)
    try {
      const apiClient = getApiClient()
      await apiClient.saveParticipantEmail(sessionId, email.trim())
      onComplete(true)
    } catch {
      setError('Could not save your email. You can skip and we\'ll still keep your image.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-md mx-auto animate-slide-up px-6 py-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-mid-green/15 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-display font-bold text-dark-green mb-2">
          Receive your image
        </h2>
        <p className="text-sm font-body text-dark-green/70 leading-relaxed">
          Would you like us to send your AI-generated vision to your inbox? Your email will only be used for this and will be automatically deleted after 90 days.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="mb-4">
          <label htmlFor="participant-email" className="block text-sm font-body font-medium text-dark-green mb-1">
            Email address
          </label>
          <input
            id="participant-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="input-denizen w-full"
            aria-describedby={error ? 'email-error' : undefined}
          />
          {error && (
            <p id="email-error" role="alert" className="mt-1 text-xs text-red-600">{error}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={!email.trim() || isSubmitting}
          className="btn-denizen-primary w-full py-3 mb-3 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving…' : 'Send me my image'}
        </button>
      </form>

      <button
        type="button"
        onClick={() => onComplete(false)}
        className="w-full py-3 text-sm font-body text-dark-green/60 hover:text-dark-green transition-colors"
      >
        No thanks, skip
      </button>
    </div>
  )
}

/**
 * Session complete component — DENiZEN
 */
interface SessionCompleteProps {
  sessionId: string
  imageUrl: string | null
  onNewSession: () => void
  onGoBack?: () => void
  influenceDescription?: string | null
}

function SessionComplete({ sessionId: _sessionId, imageUrl, onNewSession, onGoBack, influenceDescription }: SessionCompleteProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false)

  return (
    <div className="max-w-2xl mx-auto animate-slide-up px-6">
      <div className="text-center mb-3">
        <h1 className="text-3xl font-display font-bold text-dark-green mb-2">
          Session Complete
        </h1>
        <p className="text-base font-body text-dark-green/80">
          Thank you for sharing your vision of the future
        </p>
      </div>

      {/* Separator */}
      <hr className="border-dark-green/15 mb-4" />

      {/* Generated image — zoom button matches grid behaviour */}
      {imageUrl && (
        <div className="mb-6 aspect-square overflow-hidden rounded-[10px] relative group">
          <img
            src={imageUrl}
            alt="Your community vision"
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            aria-label="View image fullscreen"
            onClick={() => setLightboxOpen(true)}
            className="absolute top-3 right-3 w-11 h-11 border border-dark-green/15 rounded-full flex items-center justify-center bg-white shadow-denizen-sm transition-all duration-200 hover:scale-110 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-green opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
          >
            <svg className="w-5 h-5 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </button>
          <ImageLightbox
            imageUrl={imageUrl}
            alt="Your community vision fullscreen"
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
          />
        </div>
      )}

      {/* Influence description — how conversation shaped the image */}
      {influenceDescription && (
        <div className="mb-8">
          <InfluenceDescription
            description={influenceDescription}
            isLoading={false}
            error={null}
          />
        </div>
      )}

      <div className="denizen-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-mid-green/15 flex items-center justify-center">
            <svg className="w-4 h-4 text-mid-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <p className="text-sm font-body text-dark-green">
            Your data will be stored securely for 90 days and then automatically deleted.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={onNewSession}
            className="btn-denizen-primary w-full py-4"
          >
            <span className="flex items-center justify-center gap-2">
              Start New Session
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </button>
          {onGoBack && (
            <button
              onClick={onGoBack}
              className="btn-denizen-secondary w-full py-3"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
                Choose a Different Image
              </span>
            </button>
          )}
          <Link
            to="/join"
            className="btn-denizen-secondary w-full py-3 text-center"
          >
            Join a new session
          </Link>
        </div>
      </div>
    </div>
  )
}

export default SessionFlow
